/**
 * TaskDetailPanel - Side panel for full task editing.
 *
 * Shows when a task is selected. Allows editing all task fields inline:
 * title, description, priority, due date, due time, list, tags, and subtasks.
 * Uses useTaskStore for updates. Shows validation errors.
 *
 * @see Requirements 2.1, 2.3, 2.5, 2.8
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Priority, Subtask, Task } from '@/types';
import { useTaskStore } from '@/stores/taskStore';
import { useUIStore } from '@/stores/uiStore';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { SubtaskList } from './SubtaskList';

export interface TaskDetailPanelProps {
  /**
   * The task ID to display and edit. Ignored when `draft` is true, in which
   * case the panel renders in create mode with no backing task.
   */
  taskId?: string;
  /**
   * When true, the panel opens in create/draft mode: all fields start empty
   * and nothing is persisted until the user enters a title and saves. On save
   * the task is created via `createTask` (no junk rows are written up front).
   */
  draft?: boolean;
  /** Called when the panel should close. */
  onClose: () => void;
  /** Called after a draft task is successfully created, with its new ID. */
  onCreated?: (taskId: string) => void;
}

const PRIORITY_OPTIONS: { value: Priority; label: string; color: string }[] = [
  { value: 'none', label: 'None', color: '' },
  { value: 'low', label: 'Low', color: 'var(--color-priority-low)' },
  { value: 'medium', label: 'Medium', color: 'var(--color-priority-medium)' },
  { value: 'high', label: 'High', color: 'var(--color-priority-high)' },
];

const MAX_TAGS = 10;

// ─── JournalLink Sub-component ───────────────────────────────────────────────

interface JournalLinkProps {
  linkedJournalDate: string | null;
  onLink: (date: string) => void;
  onUnlink: () => void;
  disabled?: boolean;
}

/**
 * JournalLink — Displays the "Link to Journal" action.
 *
 * Stores ONLY a date reference (YYYY-MM-DD) on the task.
 * Never accesses or displays journal entry content.
 * Clicking the linked date navigates to the Journal module for that date.
 *
 * @see Requirements 10.2, 16.3
 */
function JournalLink({ linkedJournalDate, onLink, onUnlink, disabled }: JournalLinkProps) {
  const setActiveModule = useUIStore((s) => s.setActiveModule);
  const setSelectedDate = useUIStore((s) => s.setSelectedDate);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerDate, setPickerDate] = useState('');

  const handleLinkToday = () => {
    const today = new Date().toISOString().split('T')[0] ?? '';
    onLink(today);
  };

  const handleLinkDate = () => {
    if (pickerDate) {
      onLink(pickerDate);
      setShowDatePicker(false);
      setPickerDate('');
    }
  };

  const handleNavigateToJournal = () => {
    if (linkedJournalDate) {
      setSelectedDate(linkedJournalDate);
      setActiveModule('journal');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="flex flex-col gap-2 border-t border-border pt-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-text-secondary">
          Linked Journal Entry
        </span>
      </div>

      {linkedJournalDate ? (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleNavigateToJournal}
            className={[
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full',
              'bg-secondary-light border border-secondary text-text-primary',
              'hover:bg-secondary hover:text-white transition-all duration-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
            ].join(' ')}
            aria-label={`View journal entry for ${formatDate(linkedJournalDate)}`}
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M3 2.5A1.5 1.5 0 0 1 4.5 1h7A1.5 1.5 0 0 1 13 2.5v11a.5.5 0 0 1-.765.424L8 11.118l-4.235 2.806A.5.5 0 0 1 3 13.5v-11Z"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinejoin="round"
              />
            </svg>
            {formatDate(linkedJournalDate)}
          </button>
          {!disabled && (
            <button
              type="button"
              onClick={onUnlink}
              aria-label="Remove journal link"
              className={[
                'w-6 h-6 flex items-center justify-center rounded-full',
                'text-text-secondary hover:text-primary-dark hover:bg-primary/10',
                'transition-colors duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
              ].join(' ')}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M4 4L12 12M12 4L4 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleLinkToday}
              disabled={disabled}
            >
              <svg className="w-3.5 h-3.5 mr-1" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M3 2.5A1.5 1.5 0 0 1 4.5 1h7A1.5 1.5 0 0 1 13 2.5v11a.5.5 0 0 1-.765.424L8 11.118l-4.235 2.806A.5.5 0 0 1 3 13.5v-11Z"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinejoin="round"
                />
              </svg>
              Link to Today
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowDatePicker(!showDatePicker)}
              disabled={disabled}
            >
              Pick Date
            </Button>
          </div>
          {showDatePicker && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={pickerDate}
                onChange={(e) => setPickerDate(e.target.value)}
                aria-label="Select journal date to link"
                className={[
                  'flex-1 rounded-sm border border-border bg-bg-card text-text-primary',
                  'px-3 py-1.5 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-secondary',
                ].join(' ')}
              />
              <Button
                size="sm"
                variant="primary"
                onClick={handleLinkDate}
                disabled={!pickerDate}
              >
                Link
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function TaskDetailPanel({ taskId, draft = false, onClose, onCreated }: TaskDetailPanelProps) {
  const tasks = useTaskStore((s) => s.tasks);
  const taskLists = useTaskStore((s) => s.taskLists);
  const tags = useTaskStore((s) => s.tags);
  const createTask = useTaskStore((s) => s.createTask);
  const updateTask = useTaskStore((s) => s.updateTask);
  const completeTask = useTaskStore((s) => s.completeTask);
  const deleteTask = useTaskStore((s) => s.deleteTask);

  const task = useMemo(
    () => (taskId ? tasks.find((t) => t.taskId === taskId) : undefined),
    [tasks, taskId]
  );

  // In draft (create) mode there is no backing task yet. Completion-only UI is
  // never relevant while drafting.
  const isCompleted = task?.isCompleted ?? false;

  // Local editing state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('none');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [listId, setListId] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [subtasks, setSubtasks] = useState<Subtask[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Sync local state from task when task changes. In draft mode start empty so
  // the user titles the task themselves; nothing is persisted until save.
  useEffect(() => {
    if (draft) {
      setTitle('');
      setDescription('');
      setPriority('none');
      setDueDate('');
      setDueTime('');
      setListId('');
      setSelectedTags([]);
      setSubtasks([]);
      setErrors({});
      return;
    }
    if (task) {
      setTitle(task.title);
      setDescription(task.description);
      setPriority(task.priority);
      setDueDate(task.dueDate ?? '');
      setDueTime(task.dueTime ?? '');
      setListId(task.listId ?? '');
      setSelectedTags(task.tags);
      setSubtasks(task.subtasks);
      setErrors({});
    }
  }, [task, draft]);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required.';
    } else if (title.length > 500) {
      newErrors.title = 'Title must be 500 characters or fewer.';
    }

    if (description.length > 5000) {
      newErrors.description = 'Description must be 5000 characters or fewer.';
    }

    if (selectedTags.length > MAX_TAGS) {
      newErrors.tags = `Maximum of ${MAX_TAGS} tags allowed.`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [title, description, selectedTags]);

  const handleSave = useCallback(async () => {
    if (!validate()) return;

    setIsSaving(true);
    try {
      if (draft) {
        // Create mode: persist only now, with the title the user actually typed.
        const created = await createTask({
          title: title.trim(),
          description,
          priority,
          dueDate: dueDate || null,
          dueTime: dueTime || null,
          listId: listId || null,
          tags: selectedTags,
          subtasks,
          isCompleted: false,
          completedAt: null,
          isUrgent: false,
          isImportant: false,
          sortOrder: 0,
          linkedJournalDate: null,
          focusMinutes: 0,
          deleted: false,
          userId: '',
        });
        setErrors({});
        onCreated?.(created.taskId);
        return;
      }

      if (!taskId) return;
      const updates: Partial<Task> = {
        title: title.trim(),
        description,
        priority,
        dueDate: dueDate || null,
        dueTime: dueTime || null,
        listId: listId || null,
        tags: selectedTags,
        subtasks,
      };
      await updateTask(taskId, updates);
      setErrors({});
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save task.';
      setErrors((prev) => ({ ...prev, form: message }));
    } finally {
      setIsSaving(false);
    }
  }, [validate, draft, title, description, priority, dueDate, dueTime, listId, selectedTags, subtasks, createTask, updateTask, onCreated, taskId]);

  const handleComplete = useCallback(async () => {
    if (!taskId) return;
    try {
      await completeTask(taskId);
    } catch {
      // Error handled by store
    }
  }, [completeTask, taskId]);

  const handleDelete = useCallback(async () => {
    // In draft mode nothing was persisted, so "delete" simply discards the draft.
    if (!taskId) {
      onClose();
      return;
    }
    try {
      await deleteTask(taskId);
      onClose();
    } catch {
      // Error handled by store
    }
  }, [deleteTask, taskId, onClose]);

  const handleUncomplete = useCallback(async () => {
    if (!taskId) return;
    try {
      await updateTask(taskId, { isCompleted: false, completedAt: null });
    } catch {
      // Error handled by store
    }
  }, [updateTask, taskId]);

  const handleToggleTag = useCallback(
    (tagId: string) => {
      setSelectedTags((prev) => {
        if (prev.includes(tagId)) {
          return prev.filter((t) => t !== tagId);
        }
        if (prev.length >= MAX_TAGS) {
          setErrors((e) => ({ ...e, tags: `Maximum of ${MAX_TAGS} tags allowed.` }));
          return prev;
        }
        setErrors((e) => {
          const { tags: _tags, ...rest } = e;
          return rest;
        });
        return [...prev, tagId];
      });
    },
    []
  );

  const handleSubtasksChange = useCallback((updated: Subtask[]) => {
    setSubtasks(updated);
  }, []);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    },
    [onClose, handleSave]
  );

  if (!task && !draft) {
    return (
      <aside
        className="w-[400px] flex-shrink-0 border-l border-border bg-bg-card p-6 flex items-center justify-center"
        aria-label="Task detail panel"
      >
        <p className="text-text-secondary text-sm">Task not found.</p>
      </aside>
    );
  }

  return (
    <aside
      className="w-[400px] flex-shrink-0 border-l border-border bg-bg-card overflow-y-auto"
      aria-label="Task detail panel"
      role="complementary"
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border sticky top-0 bg-bg-card z-10">
        <h2 className="text-sm font-semibold text-text-heading">
          {draft ? 'New Task' : 'Task Details'}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close task detail panel"
          className={[
            'w-8 h-8 flex items-center justify-center rounded-sm',
            'text-text-secondary hover:text-text-primary hover:bg-hover-bg',
            'transition-colors duration-fast',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
          ].join(' ')}
        >
          <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M4 4L12 12M12 4L4 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="px-5 py-4 flex flex-col gap-5">
        {/* Form error */}
        {errors.form && (
          <div className="px-3 py-2 rounded-sm bg-primary/10 text-sm text-primary-dark" role="alert">
            {errors.form}
          </div>
        )}

        {/* Title */}
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={errors.title}
          placeholder="Task title"
          maxLength={500}
          disabled={isCompleted}
        />

        {/* Description */}
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="task-description"
            className="text-sm font-medium text-text-secondary"
          >
            Description
          </label>
          <textarea
            id="task-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add a description..."
            maxLength={5000}
            disabled={isCompleted}
            rows={3}
            aria-invalid={errors.description ? true : undefined}
            aria-describedby={errors.description ? 'desc-error' : undefined}
            className={[
              'w-full rounded-sm border bg-bg-card text-text-primary placeholder:text-text-secondary/60',
              'transition-all duration-fast ease-fast resize-y min-h-[72px]',
              'px-4 py-2 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-secondary',
              errors.description
                ? 'border-primary-dark ring-1 ring-primary-dark/30'
                : 'border-border hover:border-secondary-light',
              'disabled:opacity-50 disabled:pointer-events-none',
            ].join(' ')}
          />
          {errors.description && (
            <p id="desc-error" className="text-sm text-primary-dark" role="alert">
              {errors.description}
            </p>
          )}
          <span className="text-xs text-text-secondary self-end">
            {description.length}/5000
          </span>
        </div>

        {/* Priority */}
        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-sm font-medium text-text-secondary">Priority</legend>
          <div className="flex gap-2 flex-wrap" role="radiogroup" aria-label="Priority level">
            {PRIORITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={priority === opt.value}
                disabled={isCompleted}
                onClick={() => setPriority(opt.value)}
                className={[
                  'px-3 py-1.5 text-sm rounded-full border',
                  'transition-all duration-fast ease-default',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  priority === opt.value
                    ? 'border-primary bg-primary-light text-text-primary font-medium'
                    : 'border-border text-text-secondary hover:border-secondary-light hover:text-text-primary',
                ].join(' ')}
              >
                {opt.color && (
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5"
                    style={{ backgroundColor: opt.color }}
                    aria-hidden="true"
                  />
                )}
                {opt.label}
              </button>
            ))}
          </div>
        </fieldset>

        {/* Due Date & Time */}
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            disabled={isCompleted}
            size="sm"
          />
          <Input
            label="Due Time"
            type="time"
            value={dueTime}
            onChange={(e) => setDueTime(e.target.value)}
            disabled={isCompleted}
            size="sm"
          />
        </div>

        {/* List */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="task-list" className="text-sm font-medium text-text-secondary">
            List
          </label>
          <select
            id="task-list"
            value={listId}
            onChange={(e) => setListId(e.target.value)}
            disabled={isCompleted}
            className={[
              'w-full rounded-sm border bg-bg-card text-text-primary',
              'px-4 py-2 text-sm',
              'transition-all duration-fast ease-fast',
              'focus:outline-none focus:ring-2 focus:ring-focus-ring focus:border-secondary',
              'border-border hover:border-secondary-light',
              'disabled:opacity-50 disabled:pointer-events-none',
            ].join(' ')}
          >
            <option value="">No list</option>
            {taskLists.map((list) => (
              <option key={list.listId} value={list.listId}>
                {list.name}
              </option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-text-secondary">
              Tags
            </span>
            <span className="text-xs text-text-secondary">
              {selectedTags.length}/{MAX_TAGS}
            </span>
          </div>
          {errors.tags && (
            <p className="text-sm text-primary-dark" role="alert">
              {errors.tags}
            </p>
          )}
          {tags.length > 0 ? (
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Tag selection">
              {tags.map((tag) => {
                const isSelected = selectedTags.includes(tag.tagId);
                return (
                  <button
                    key={tag.tagId}
                    type="button"
                    role="checkbox"
                    aria-checked={isSelected}
                    aria-label={`Tag: ${tag.name}`}
                    disabled={isCompleted}
                    onClick={() => handleToggleTag(tag.tagId)}
                    className={[
                      'inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border',
                      'transition-all duration-fast ease-default',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      isSelected
                        ? 'border-primary bg-primary-light font-medium'
                        : 'border-border hover:border-secondary-light',
                    ].join(' ')}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                      aria-hidden="true"
                    />
                    <span className="text-text-primary">{tag.name}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-text-secondary">No tags available. Create tags to categorize your tasks.</p>
          )}
        </div>

        {/* Subtasks */}
        <div className="border-t border-border pt-4">
          <SubtaskList
            subtasks={subtasks}
            onChange={handleSubtasksChange}
            disabled={isCompleted}
          />
        </div>

        {/* Link to Journal */}
        {/* Journal link, focus time, and completed badge only apply to a
            persisted task — hidden while drafting a brand-new task. */}
        {task && (
          <>
            <JournalLink
              linkedJournalDate={task.linkedJournalDate}
              onLink={(date) => {
                updateTask(task.taskId, { linkedJournalDate: date });
              }}
              onUnlink={() => {
                updateTask(task.taskId, { linkedJournalDate: null });
              }}
              disabled={isCompleted}
            />

            {/* Focus time (read-only) */}
            {task.focusMinutes > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-sm bg-bg-secondary">
                <svg className="w-4 h-4 text-primary" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M8 5V8L10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span className="text-sm text-text-secondary">
                  Focus Time: <span className="font-medium text-text-primary">{task.focusMinutes} min</span>
                </span>
              </div>
            )}

            {/* Completed badge */}
            {task.isCompleted && task.completedAt && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-sm bg-bg-secondary">
                <Badge text="Completed" color="var(--color-success)" size="sm" />
                <span className="text-xs text-text-secondary">
                  {new Date(task.completedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer actions */}
      <div className="px-5 py-4 border-t border-border sticky bottom-0 bg-bg-card flex items-center gap-2">
        {!isCompleted && (
          <>
            <Button
              size="sm"
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : draft ? 'Create Task' : 'Save'}
            </Button>
            {!draft && (
              <Button
                size="sm"
                variant="secondary"
                onClick={handleComplete}
              >
                Complete
              </Button>
            )}
          </>
        )}
        {isCompleted && (
          <Button
            size="sm"
            variant="secondary"
            onClick={handleUncomplete}
          >
            ↩ Reopen Task
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={handleDelete}
          className="ml-auto text-primary-dark hover:bg-primary/10"
        >
          {draft ? 'Cancel' : 'Delete'}
        </Button>
      </div>
    </aside>
  );
}
